// Lightweight inline-SVG sparkline. Zero dependencies — fits the brand
// aesthetic (calm + minimal) and avoids pulling chartjs/recharts for what is
// essentially a polyline with a fill underneath.
//
// Renders a viewport-scaled line + filled area. All input values are
// non-negative integers; an all-zero series renders as a flat baseline.
//
// Heights of 30–60px work well in card layouts. Width adapts to container.

interface SparklinePoint {
  date: string;
  count: number;
}

interface SparklineProps {
  data: SparklinePoint[];
  height?: number;
  stroke?: string;
  fill?: string;
  ariaLabel?: string;
}

export function Sparkline({
  data,
  height = 48,
  stroke = 'var(--accent)',
  fill = 'rgba(43, 155, 195, 0.12)',
  ariaLabel,
}: SparklineProps) {
  if (data.length === 0) {
    return <div style={{ height, color: 'var(--fg-mute)', fontSize: 12 }}>No data</div>;
  }

  // Use a fixed viewBox width — the SVG scales preserveAspectRatio='none' to
  // fill the parent. This avoids needing a measured container width.
  const W = 240;
  const H = height;
  const PAD = 2; // 2px breathing room top + bottom

  const max = Math.max(1, ...data.map((d) => d.count));
  const stepX = data.length > 1 ? W / (data.length - 1) : 0;
  const yFor = (v: number): number => {
    const norm = v / max;
    return H - PAD - norm * (H - PAD * 2);
  };

  const points = data.map((d, i) => `${i * stepX},${yFor(d.count)}`);
  const linePath = `M ${points.join(' L ')}`;
  const areaPath = `${linePath} L ${(data.length - 1) * stepX},${H} L 0,${H} Z`;

  return (
    <svg
      role="img"
      aria-label={ariaLabel ?? 'Sparkline'}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height, display: 'block' }}
    >
      <path d={areaPath} fill={fill} />
      <path d={linePath} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}
